import { useState, useEffect, useMemo, useRef } from 'react'
import { Icon } from '@iconify/react'
import { HorizontalScrollWithArrows } from '@/components/HorizontalScrollWithArrows'
import { PhoneWhatsAppLink } from '@/components/PhoneWhatsAppLink'
import {
  getDistributedPrizes,
  DISTRIBUTED_PRIZES_PER_PAGE,
  type DonorPrize,
  type RouletteDistributedPrizesPagination,
  type RoulettePrizeType,
} from '@/lib/roulette'
import { useRouletteWinnerSharePng } from '@/hooks/useRouletteWinnerSharePng'
import { getRouletteShareDownloadAriaLabel } from '@/lib/share-card/rouletteShareCardCopy'
import {
  distributedPrizesDateRangeErrorMessage,
  getDefaultDistributedPrizesFilters,
  getDistributedPrizesDateRangeIssue,
  hasActiveDistributedPrizesFilters,
  type DistributedPrizesFiltersState,
} from '@/lib/distributed-prizes-filters'
import { displayNonePrizeHistoryLabel } from '@/lib/rouletteNoneDisplay'
import { RoletaDistributedPrizesFilters } from './RoletaDistributedPrizesFilters'
import { RoletaDistributedPrizeStatusCell } from './RoletaDistributedPrizeStatusCell'
import styles from './CampanhasPage.module.css'

export interface RoletaDistributedPrizesTableProps {
  campaignId: number
  token: string | null
  canUpdateStatus?: boolean
  /** Oculta cabeçalho e borda do card quando embutido em outra seção (ex.: página Ganhadores). */
  embedded?: boolean
}

const PRIZE_TYPE_LABELS: Record<RoulettePrizeType, string> = {
  coupon: 'Cupom',
  lucky_number: 'Número da sorte',
  pix: 'PIX',
  none: 'Sem prêmio',
}

function formatDate(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString('pt-BR')
}

function payloadSummary(prize: DonorPrize): string {
  const p = prize.prize_payload
  if (!p || typeof p !== 'object') return '-'
  if (prize.prize_type === 'coupon' && 'code' in p) return `Código: ${String(p.code)}`
  if (prize.prize_type === 'pix' && 'amount' in p) return `R$ ${Number(p.amount).toFixed(2)}`
  if (prize.prize_type === 'lucky_number' && 'quantity' in p) {
    const numbers = 'lucky_numbers' in p && Array.isArray(p.lucky_numbers) ? p.lucky_numbers.join(' · ') : ''
    return `${p.quantity} número(s) ganhos na roleta${numbers ? ` · Entregues: ${numbers}` : ''}`
  }
  if (prize.prize_type === 'none') {
    return 'label' in p
      ? displayNonePrizeHistoryLabel({ label: String(p.label) })
      : displayNonePrizeHistoryLabel(null)
  }
  return '-'
}

export function RoletaDistributedPrizesTable({
  campaignId,
  token,
  canUpdateStatus = true,
  embedded = false,
}: RoletaDistributedPrizesTableProps) {
  const [prizes, setPrizes] = useState<DonorPrize[]>([])
  const [pagination, setPagination] = useState<RouletteDistributedPrizesPagination | null>(null)
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<DistributedPrizesFiltersState>(
    getDefaultDistributedPrizesFilters
  )
  const [emailInput, setEmailInput] = useState('')
  const [debouncedEmail, setDebouncedEmail] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const skipEmailPageResetRef = useRef(true)
  const { sharingPrizeId, shareError, shareWinnerPng, clearShareError } =
    useRouletteWinnerSharePng(token)

  useEffect(() => {
    clearShareError()
  }, [campaignId, clearShareError])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const nextEmail = emailInput.trim()
      setDebouncedEmail(nextEmail)
      if (skipEmailPageResetRef.current) {
        skipEmailPageResetRef.current = false
      } else {
        setPage(1)
      }
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [emailInput])

  const appliedFilters = useMemo(
    (): DistributedPrizesFiltersState => ({
      ...filters,
      email: debouncedEmail,
    }),
    [filters, debouncedEmail]
  )

  const dateRangeIssue = useMemo(
    () => getDistributedPrizesDateRangeIssue(appliedFilters),
    [appliedFilters]
  )

  const dateRangeValid = dateRangeIssue === 'none'
  const dateRangeErrorMessage = distributedPrizesDateRangeErrorMessage(dateRangeIssue)

  const filtersActive = hasActiveDistributedPrizesFilters(appliedFilters)

  useEffect(() => {
    if (!dateRangeValid) {
      setPrizes([])
      setPagination(null)
      setIsLoading(false)
      return
    }

    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      setErrorMessage(null)
      try {
        const result = await getDistributedPrizes(
          campaignId,
          {
            page,
            per_page: DISTRIBUTED_PRIZES_PER_PAGE,
            status: appliedFilters.status || undefined,
            prize_type: appliedFilters.prizeType || undefined,
            email: appliedFilters.email || undefined,
            date_from: appliedFilters.dateFrom || undefined,
            date_to: appliedFilters.dateTo || undefined,
          },
          token ?? undefined
        )
        if (cancelled) return
        setPrizes(result.prizes)
        setPagination(result.pagination)
      } catch {
        if (cancelled) return
        setErrorMessage('Não foi possível carregar os prêmios distribuídos.')
        setPrizes([])
        setPagination(null)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [campaignId, token, page, appliedFilters, dateRangeValid])

  const handleFiltersChange = (next: DistributedPrizesFiltersState) => {
    setFilters(next)
    setPage(1)
  }

  const handleClearFilters = () => {
    skipEmailPageResetRef.current = true
    setFilters(getDefaultDistributedPrizesFilters())
    setEmailInput('')
    setDebouncedEmail('')
    setPage(1)
  }

  const handlePrizeStatusUpdated = (updated: DonorPrize) => {
    setPrizes((current) =>
      current.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
    )
  }

  const totalRegistros = pagination?.total ?? 0

  const content = (
    <>
      {!embedded ? (
        <div className={styles.panelHeader}>
          <div>
            <h3 id="roleta-distributed-title" className={styles.sectionTitle}>
              Prêmios distribuídos
            </h3>
            <p className={styles.sectionSubtitle}>
              Listagem dos prêmios já sorteados. Números e cupons têm entrega automática; prêmios PIX permitem registrar a entrega manual.
              {totalRegistros > 0 ? ` ${totalRegistros} registro(s) no total.` : ''}
            </p>
          </div>
        </div>
      ) : totalRegistros > 0 ? (
        <p className={styles.sectionSubtitle}>
          {totalRegistros} registro(s) no total.
        </p>
      ) : null}

      <RoletaDistributedPrizesFilters
        value={filters}
        emailInput={emailInput}
        onEmailInputChange={setEmailInput}
        onChange={handleFiltersChange}
        onClear={handleClearFilters}
      />

      <p className={styles.roletaFiltersHint}>
        Os filtros sao aplicados no servidor. Aguarde um instante apos digitar o e-mail.
      </p>

      {dateRangeErrorMessage ? (
        <div className={styles.errorBanner} role="alert">
          {dateRangeErrorMessage}
        </div>
      ) : null}

      {errorMessage && dateRangeValid ? (
        <div className={styles.errorBanner} role="alert">
          {errorMessage}
        </div>
      ) : null}

      {shareError ? (
        <div className={styles.errorBanner} role="alert">
          {shareError}
        </div>
      ) : null}

      {isLoading ? (
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <span>Carregando...</span>
        </div>
      ) : prizes.length === 0 && !errorMessage && dateRangeValid ? (
        <div className={styles.emptyPanel}>
          <div className={styles.emptyPanelIcon}>
            <Icon icon="mdi:gift-outline" />
          </div>
          <h4>
            {filtersActive
              ? 'Nenhum resultado com os filtros atuais'
              : 'Nenhum prêmio distribuído ainda'}
          </h4>
          <p>
            {filtersActive
              ? 'Ajuste os filtros ou limpe a busca para ver mais registros.'
              : 'Os prêmios sorteados aparecerão aqui.'}
          </p>
        </div>
      ) : dateRangeValid && !errorMessage ? (
        <>
          <p className={styles.roletaScrollHint}>
            <Icon icon="mdi:gesture-swipe-horizontal" aria-hidden />
            Deslize ou use as setas para ver todas as colunas
          </p>
          <div className={styles.roletaTableRegion}>
          <HorizontalScrollWithArrows scrollClassName={styles.roletaTableWrap}>
            <table className={styles.roletaTable}>
              <thead>
                <tr>
                  <th>Comprador</th>
                  <th>WhatsApp</th>
                  <th>E-mail</th>
                  <th>Tipo</th>
                  <th>Resumo</th>
                  <th>Status</th>
                  <th>Data</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {prizes.map((prize) => {
                  const isSharing = sharingPrizeId === prize.id
                  const downloadAriaLabel = getRouletteShareDownloadAriaLabel(
                    prize.prize_type,
                    prize.donor?.name,
                    prize.id
                  )

                  return (
                  <tr key={prize.id}>
                    <td>{prize.donor?.name ?? '-'}</td>
                    <td>
                      <PhoneWhatsAppLink
                        phone={prize.donor?.phone}
                        contactName={prize.donor?.name}
                      />
                    </td>
                    <td>{prize.donor?.email ?? '-'}</td>
                    <td>{PRIZE_TYPE_LABELS[prize.prize_type]}</td>
                    <td>{payloadSummary(prize)}</td>
                    <td>
                      <RoletaDistributedPrizeStatusCell
                        campaignId={campaignId}
                        prize={prize}
                        token={token}
                        disabled={isLoading}
                        readOnly={!canUpdateStatus}
                        onUpdated={handlePrizeStatusUpdated}
                      />
                    </td>
                    <td>{formatDate(prize.created_at)}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.roletaShareBtn}
                        disabled={isLoading || isSharing || sharingPrizeId !== null}
                        aria-label={downloadAriaLabel}
                        title="Baixar imagem"
                        onClick={() => void shareWinnerPng(campaignId, prize.id)}
                      >
                        {isSharing ? (
                          <span className={styles.roletaShareBtnSpinner} aria-hidden />
                        ) : (
                          <Icon icon="mdi:download" aria-hidden />
                        )}
                      </button>
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </HorizontalScrollWithArrows>
          </div>

          {pagination && pagination.last_page > 1 ? (
            <div className={styles.roletaPagination}>
              <button
                type="button"
                className={styles.roletaPageBtn}
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
              >
                Anterior
              </button>
              <span className={styles.roletaPaginationInfo}>
                Página {pagination.current_page} de {pagination.last_page}
              </span>
              <button
                type="button"
                className={styles.roletaPageBtn}
                disabled={page >= pagination.last_page || isLoading}
                onClick={() => setPage((currentPage) => currentPage + 1)}
              >
                Próxima
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  )

  if (embedded) {
    return <div className={styles.roletaEmbedded}>{content}</div>
  }

  return (
    <section className={styles.panelCard} aria-labelledby="roleta-distributed-title">
      {content}
    </section>
  )
}
